import type { VendorBill } from "../../services/financialService";
import { StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { byNumber, byText, reversed, type RegisterConfig } from "../register/registerLogic";

const today = () => new Date().toISOString().slice(0, 10);

/** Where a bill is: its approval step until approved, then how much of it is paid. */
export function billStage(bill: VendorBill): { key: string; label: string; dot: string } {
  const workflow = bill.workflow_status ?? "";
  if (workflow === "draft") return { key: "draft", label: "Draft", dot: "bg-gray-400" };
  if (workflow === "returned") return { key: "returned", label: "Returned for changes", dot: "bg-amber-500" };
  if (workflow === "submitted") return { key: "submitted", label: "Awaiting review", dot: "bg-sky-500" };
  if (workflow === "verified") return { key: "verified", label: "Awaiting approval", dot: "bg-indigo-500" };
  if (workflow === "rejected") return { key: "rejected", label: "Rejected", dot: "bg-red-500" };
  if (bill.status === "paid" || Number(bill.balance_due) <= 0) return { key: "paid", label: "Paid", dot: "bg-emerald-500" };
  if (billOverdue(bill)) return { key: "overdue", label: "Overdue", dot: "bg-red-500" };
  if (bill.status === "part_paid") return { key: "part_paid", label: "Part paid", dot: "bg-amber-500" };
  return { key: "unpaid", label: "Approved, unpaid", dot: "bg-amber-500" };
}

export const billPayable = (bill: VendorBill) => ["approved", "part_paid"].includes(bill.status) && Number(bill.balance_due) > 0;

export const billOverdue = (bill: VendorBill) => billPayable(bill) && bill.due_date < today();

export const billNumber = (bill: VendorBill) => bill.internal_number || bill.bill_number;

export const payablesConfig: RegisterConfig<VendorBill> = {
  search: (bill) =>
    [bill.internal_number, bill.bill_number, bill.supplier_name, bill.description, bill.purchase_order_number, bill.prepared_by_name].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "preparing", label: "Drafts & returned", match: (bill) => ["draft", "returned"].includes(bill.workflow_status ?? "") },
    { key: "review", label: "Awaiting review", match: (bill) => bill.workflow_status === "submitted" },
    { key: "approval", label: "Awaiting approval", match: (bill) => bill.workflow_status === "verified" },
    { key: "topay", label: "To pay", match: billPayable },
    { key: "overdue", label: "Overdue", match: billOverdue },
    { key: "paid", label: "Paid", match: (bill) => billStage(bill).key === "paid" },
  ],
  date: { label: "Due", value: (bill) => bill.due_date },
  sorts: [
    { key: "due", label: "Due soonest", compare: byText<VendorBill>((bill) => bill.due_date) },
    { key: "-date", label: "Newest bill first", compare: reversed(byText<VendorBill>((bill) => bill.bill_date)) },
    { key: "-balance", label: "Balance (highest first)", compare: reversed(byNumber<VendorBill>((bill) => bill.balance_due)) },
    { key: "supplier", label: "Supplier (A–Z)", compare: byText<VendorBill>((bill) => bill.supplier_name) },
  ],
};

const money = (value: string | number) => Number(value || 0).toLocaleString();

export const payablesColumns: RegisterColumn<VendorBill>[] = [
  { header: "Bill no.", cell: (bill) => <span className="font-mono text-xs">{billNumber(bill)}</span>, exportValue: billNumber },
  { header: "Supplier", cell: (bill) => <span className="font-medium text-gray-900">{bill.supplier_name}</span>, exportValue: (bill) => bill.supplier_name, className: "max-w-[12rem] truncate" },
  { header: "Their invoice", cell: (bill) => <span className="font-mono text-xs text-gray-600">{bill.bill_number}</span>, exportValue: (bill) => bill.bill_number },
  { header: "For", cell: (bill) => bill.description, exportValue: (bill) => bill.description, className: "max-w-[16rem] truncate text-gray-600" },
  { header: "Due", cell: (bill) => <>{bill.due_date}{billOverdue(bill) && <span className="ml-1.5 text-xs font-medium text-red-600">late</span>}</>, exportValue: (bill) => bill.due_date, className: "tabular-nums text-gray-700" },
  { header: "Total (UGX)", cell: (bill) => money(bill.total_amount), exportValue: (bill) => Number(bill.total_amount), align: "right" },
  {
    header: "Balance (UGX)",
    cell: (bill) => (Number(bill.balance_due) > 0 ? <span className="font-semibold text-gray-900">{money(bill.balance_due)}</span> : <span className="text-gray-300">—</span>),
    exportValue: (bill) => Number(bill.balance_due),
    align: "right",
  },
  { header: "Stage", cell: (bill) => <StatusDot {...billStage(bill)} />, exportValue: (bill) => billStage(bill).label },
];
